/*
  Feerway offer card (snippets/fw-bogo.liquid): buy one get one free, gift with purchase, or 1-or-2 at a pair price.
  Keeps the card in step with the chosen variant, lets the shopper pick the second item's colour, and turns the product
  form's single "id" into items[] (first item, free item, optional gift) through the form's `formdata` event, so
  Shrine's product-form.js and the sticky button keep working unchanged. Discounts come from Shopify, not from here.
*/
if (!customElements.get('fw-bogo')) {
  customElements.define(
    'fw-bogo',
    class FwBogo extends HTMLElement {
      connectedCallback() {
        this.variants = JSON.parse(this.querySelector('[data-fw-bogo-variants]').textContent);
        this.match = Number(this.dataset.match);
        this.choice = Number(this.dataset.choice);
        this.freeSecond = this.dataset.freeSecond !== 'false';
        this.pairSaving = Number(this.dataset.pairSaving || 0);
        this.tier = 1;
        this.querySelectorAll('.fw-bogo__tier-radio').forEach((radio) =>
          radio.addEventListener('change', () => {
            this.tier = Number(radio.value);
            this.update();
          })
        );
        this.form = document.getElementById(this.dataset.form);
        this.radios = Array.from(this.querySelectorAll('.fw-bogo__radio'));
        this.secondTouched = false;

        this.radios.forEach((radio) =>
          radio.addEventListener('change', () => {
            this.secondTouched = true;
            this.update();
            const second = this.secondVariant();
            if (second) this.querySelector('[data-fw-live]').textContent = radio.value;
          })
        );

        this.onFormData = this.onFormData.bind(this);
        if (this.form) this.form.addEventListener('formdata', this.onFormData);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data.sectionId !== this.dataset.section || !event.data.variant) return;
            this.current = this.variants.find((v) => v.id === event.data.variant.id) || this.current;
            this.update();
          });
        }

        const idInput = this.form && this.form.querySelector('[name="id"]');
        const id = idInput ? Number(idInput.value) : null;
        this.current = this.variants.find((v) => v.id === id) || this.variants.find((v) => v.available) || this.variants[0];
        this.update();
      }

      disconnectedCallback() {
        if (this.form) this.form.removeEventListener('formdata', this.onFormData);
        if (this.unsubscribe) this.unsubscribe();
      }

      variantWith(value) {
        const first = this.current;
        return this.variants.find((v) =>
          v.options.every((option, i) => (i === this.choice ? option === value : option === first.options[i]))
        );
      }

      secondVariant() {
        if (this.choice < 0) return this.current;
        const checked = this.radios.find((radio) => radio.checked);
        const variant = this.variantWith(checked ? checked.value : this.current.options[this.choice]);
        return variant && variant.available ? variant : null;
      }

      update() {
        const first = this.current;
        if (!first) return;

        this.radios.forEach((radio) => {
          const variant = this.variantWith(radio.value);
          radio.disabled = !variant || !variant.available;
        });
        if (!this.secondTouched) {
          const same = this.radios.find((radio) => radio.value === first.options[this.choice]);
          if (same && !same.disabled) same.checked = true;
        }
        const checked = this.radios.find((radio) => radio.checked);
        if (checked && checked.disabled) {
          const fallback = this.radios.find((radio) => !radio.disabled);
          if (fallback) fallback.checked = true;
        }

        const second = this.secondVariant() || first;
        let price = this.money(first.price);
        if (this.pairSaving > 0) {
          const two = this.tier === 2;
          const pairTotal = first.price + second.price - this.pairSaving;
          this.setText('[data-fw-tier-one]', this.money(first.price));
          this.setText('[data-fw-tier-two]', this.money(pairTotal));
          this.setText('[data-fw-tier-each]', (this.dataset.eachText || '').replace('[price]', this.money(Math.round(pairTotal / 2))));
          this.querySelectorAll('[data-fw-second-line], [data-fw-saving-line]').forEach((line) => {
            line.hidden = !two;
          });
          this.setText('[data-fw-second-price]', this.money(second.price));
          this.setText('[data-fw-first-price]', this.money(first.price));
          this.setText('[data-fw-pair-label]', two ? this.dataset.pairLabel : this.dataset.singleLabel);
          if (two) price = this.money(pairTotal);
          this.setText('[data-fw-pair]', price);
          this.setText('[data-fw-first]', first.options.join(' / '));
        } else {
          this.setText('[data-fw-pair]', price);
          this.setText('[data-fw-first]', first.options.join(' / '));
          this.setText('[data-fw-first-price]', price);
        }
        this.setText('[data-fw-each]', (this.dataset.eachLabel || '').replace('[price]', this.money(Math.floor(first.price / 2))));
        if (this.match >= 0) this.setText('[data-fw-match]', first.options[this.match]);
        const was = this.querySelector('[data-fw-second-was]');
        if (was) was.lastChild.textContent = this.money(second.price);

        this.setThumb('[data-fw-first-thumb] img', first.image);
        this.setThumb('[data-fw-second-thumb] img', second.image);
        this.updateButtons(first, price);
      }

      setThumb(selector, src) {
        const img = this.querySelector(selector);
        if (!img || !src || img.getAttribute('src') === src) return;
        img.removeAttribute('srcset');
        img.src = src;
      }

      updateButtons(first, price) {
        const label = this.tier === 2 && this.dataset.buttonLabelPair ? this.dataset.buttonLabelPair : this.dataset.buttonLabel;
        if (!label || !first.available) return;
        const text = `${label} – ${price}`;
        const main = document.querySelector(`#ProductSubmitButton-${this.dataset.section} > span`);
        if (main) main.textContent = text;
        document.querySelectorAll(`.sticky-atc__button button[form="${this.dataset.form}"] > span`).forEach((span) => {
          span.textContent = text;
        });
      }

      onFormData(event) {
        const first = this.current;
        const second = this.secondVariant();
        const withSecond = this.freeSecond || (this.pairSaving > 0 && this.tier === 2);
        if (!first || (withSecond && !second)) return;
        const data = event.formData;
        data.delete('id');
        data.delete('quantity');
        const items = withSecond ? [first.id, second.id] : [first.id];
        items.forEach((id, i) => {
          data.append(`items[${i}][id]`, id);
          data.append(`items[${i}][quantity]`, 1);
        });
        if (this.dataset.gift) {
          const i = items.length;
          data.append(`items[${i}][id]`, this.dataset.gift);
          data.append(`items[${i}][quantity]`, 1);
          data.append(`items[${i}][properties][_fw_gift_for]`, this.dataset.productId);
        }
      }

      setText(selector, text) {
        const el = this.querySelector(selector);
        if (el) el.textContent = text;
      }

      money(cents) {
        const format = this.dataset.moneyFormat || '{{amount}}';
        const value = (cents / 100).toFixed(2);
        const [whole, fraction] = value.split('.');
        const grouped = (sep) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
          if (key === 'amount_no_decimals') return grouped(',');
          if (key === 'amount_with_comma_separator') return `${grouped('.')},${fraction}`;
          if (key === 'amount_no_decimals_with_comma_separator') return grouped('.');
          return `${grouped(',')}.${fraction}`;
        });
      }
    }
  );
}
